import React, { useEffect, useRef } from "react";

/**
 * Minimal focus-trap wrapper for modals.
 *
 * Behavior:
 *   - On mount, focuses the first tabbable element inside the trap (or the
 *     container itself when no focusable child exists).
 *   - On unmount, restores focus to whatever element was focused before the
 *     trap opened (typically the button that opened the modal).
 *   - Intercepts Tab / Shift+Tab and cycles focus inside the trap.
 *
 * Notes:
 *   - Escape handling is left to the host modal — every Altiv modal already
 *     wires Escape via its own keydown handler or `useEffect` listener.
 *   - The trap is opt-in: pass `active={false}` to disable without unmounting
 *     (useful while a modal is animating out).
 *
 * Usage:
 *   <FocusTrap>
 *     <div className="altiv-modal-card">...</div>
 *   </FocusTrap>
 */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export default function FocusTrap({ active = true, children, className, ...rest }) {
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const container = containerRef.current;
    if (!container) return undefined;

    // Defer to next frame so the modal content has actually rendered.
    const focusFirst = () => {
      const focusables = container.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        container.setAttribute("tabindex", "-1");
        container.focus();
      }
    };
    const rafId = requestAnimationFrame(focusFirst);

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(rafId);
      container.removeEventListener("keydown", handleKeyDown);
      const previously = previouslyFocusedRef.current;
      if (previously && typeof previously.focus === "function") {
        previously.focus();
      }
    };
  }, [active]);

  // Forward any extra props (role, aria-modal, style, onClick, data-testid…)
  // onto the container so callers can use <FocusTrap> as a drop-in replacement
  // for their modal-shell <div> without losing dialog semantics.
  return (
    <div ref={containerRef} className={className} {...rest}>
      {children}
    </div>
  );
}
